import { z } from "zod";

export const agentIds = ["melo", "peac", "kiw", "pomel"] as const;
export const AgentIdSchema = z.enum(agentIds);
export type AgentId = z.infer<typeof AgentIdSchema>;

export const agentCatalog = {
  melo: {
    id: "melo",
    name: "Melo",
    reading: "メロ",
    model: "GPT-5.6 Sol · Fast",
    effort: "Medium",
    availability: "active",
  },
  peac: {
    id: "peac",
    name: "Peac",
    reading: "ピーク",
    model: "GPT-5.6 Sol · Standard",
    effort: "XHigh",
    availability: "limited",
  },
  kiw: {
    id: "kiw",
    name: "Kiw",
    reading: "キウ",
    model: "GPT-5.6 Luna · Fast",
    effort: "High",
    availability: "active",
  },
  pomel: {
    id: "pomel",
    name: "Pomel",
    reading: "ポメル",
    model: "GPT-5.6 Sol",
    effort: "High",
    availability: "owner-only",
  },
} as const satisfies Record<AgentId, {
  id: AgentId;
  name: string;
  reading: string;
  model: string;
  effort: string;
  availability: "active" | "limited" | "owner-only";
}>;

export const SessionModeSchema = z.enum(["conversation", "observer"]);
export type SessionMode = z.infer<typeof SessionModeSchema>;

export const AudioModeSchema = z.enum(["conversation", "observer", "muted"]);
export type AudioMode = z.infer<typeof AudioModeSchema>;

export function audioModeFor(mode: SessionMode, speakerMuted: boolean): AudioMode {
  return speakerMuted ? "muted" : mode;
}

export function nextAudioMode(current: AudioMode): AudioMode {
  if (current === "conversation") return "observer";
  if (current === "observer") return "muted";
  return "conversation";
}

export const SessionSurfaceSchema = z.enum(["web", "meet"]);
export type SessionSurface = z.infer<typeof SessionSurfaceSchema>;

export const ThreadStatusSchema = z.enum(["open", "closed"]);
export type ThreadStatus = z.infer<typeof ThreadStatusSchema>;

export const ThreadTurnRoleSchema = z.enum(["user", "assistant", "tool", "system"]);
export type ThreadTurnRole = z.infer<typeof ThreadTurnRoleSchema>;

export const ThreadTurnSourceSchema = z.enum([
  "speech_transcript",
  "meeting_transcript",
  "typed",
  "assistant_final",
  "tool_result",
  "system",
]);
export type ThreadTurnSource = z.infer<typeof ThreadTurnSourceSchema>;

export const ThreadTurnSchema = z.object({
  id: z.string().min(1).max(128),
  clientEventId: z.string().min(1).max(128),
  ordinal: z.number().int().positive(),
  role: ThreadTurnRoleSchema,
  text: z.string().max(100_000),
  source: ThreadTurnSourceSchema,
  surface: SessionSurfaceSchema,
  agent: AgentIdSchema.optional(),
  at: z.string().datetime(),
  receivedAt: z.string().datetime(),
});
export type ThreadTurn = z.infer<typeof ThreadTurnSchema>;

export const ThreadRecordSchema = z.object({
  schemaVersion: z.literal(2),
  id: z.string().uuid(),
  slackTeamId: z.string().min(1),
  requesterSlackUserId: z.string().min(1),
  personId: z.string().min(1),
  agent: AgentIdSchema,
  localDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/u),
  timezone: z.string().min(1).max(100),
  sequence: z.number().int().positive(),
  status: ThreadStatusSchema,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  closedAt: z.string().datetime().optional(),
  initialSurface: SessionSurfaceSchema,
  lastSurface: SessionSurfaceSchema,
  label: z.string().max(100),
  lastUserText: z.string().max(100_000).optional(),
  turnCount: z.number().int().nonnegative(),
  migrationSourceSessionId: z.string().optional(),
  meetingWorkspaceId: z.string().uuid().optional(),
});
export type ThreadRecord = z.infer<typeof ThreadRecordSchema>;

export const ThreadSummarySchema = ThreadRecordSchema.omit({ lastUserText: true });
export type ThreadSummary = z.infer<typeof ThreadSummarySchema>;

export const ThreadTurnInputSchema = z.object({
  clientEventId: z.string().min(8).max(128),
  role: ThreadTurnRoleSchema,
  text: z.string().trim().min(1).max(100_000),
  source: ThreadTurnSourceSchema,
  surface: SessionSurfaceSchema,
  agent: AgentIdSchema.optional(),
  at: z.string().datetime().optional(),
});
export type ThreadTurnInput = z.infer<typeof ThreadTurnInputSchema>;

export const editableProfileSchema = z.object({
  preferredName: z.string().trim().max(80),
  role: z.string().trim().max(200),
  responsibilities: z.string().trim().max(1_000),
  selfIntro: z.string().trim().max(2_000),
  publicIntro: z.string().trim().max(2_000),
  internalProfile: z.string().trim().max(2_000),
});
export type EditableProfile = z.infer<typeof editableProfileSchema>;

export const voiceIds = ["alloy", "ash", "ballad", "coral", "echo", "sage", "shimmer", "verse", "marin", "cedar"] as const;
export const VoiceIdSchema = z.enum(voiceIds);
export type VoiceId = z.infer<typeof VoiceIdSchema>;

export const UserSettingsSchema = z.object({
  version: z.number().int().positive(),
  timezone: z.string().min(1).max(100),
  profile: editableProfileSchema,
  voiceByAgent: z.record(AgentIdSchema, VoiceIdSchema),
  bgmEnabled: z.boolean(),
  bgmVolume: z.number().min(0).max(0.2),
  updatedAt: z.string().datetime(),
});
export type UserSettings = z.infer<typeof UserSettingsSchema>;

export const SettingsUpdateSchema = UserSettingsSchema.pick({
  version: true,
  timezone: true,
  profile: true,
  voiceByAgent: true,
  bgmEnabled: true,
  bgmVolume: true,
});
export type SettingsUpdate = z.infer<typeof SettingsUpdateSchema>;

export const RequesterContextSchema = z.object({
  slackTeamId: z.string(),
  slackUserId: z.string(),
  personId: z.string(),
  slackDisplayName: z.string(),
  email: z.string().email(),
  owner: z.boolean(),
  preferredName: z.string(),
  managedRole: z.string(),
  responsibilities: z.string(),
  selfIntro: z.string(),
  publicIntro: z.string(),
  internalProfile: z.string(),
  agents: z.array(AgentIdSchema),
  capabilities: z.array(z.string()),
});
export type RequesterContext = z.infer<typeof RequesterContextSchema>;

export const WakeAgentEventSchema = z.object({
  type: z.literal("wake_agent"),
  agent: AgentIdSchema,
  source: z.enum(["voice", "double_click", "long_press"]),
  at: z.string().datetime(),
});
export type WakeAgentEvent = z.infer<typeof WakeAgentEventSchema>;

export const MeetingSpeakerTranscriptSchema = z.object({
  speakerId: z.string().trim().min(1).max(300),
  speakerName: z.string().trim().min(1).max(200),
  text: z.string().trim().min(1).max(20_000),
  source: z.enum(["meet_media_api", "tab_capture"]),
  attribution: z.enum(["account", "participant", "unattributed"]),
  authority: z.literal("context_only"),
  accountId: z.string().trim().min(1).max(300).optional(),
  sessionName: z.string().trim().max(500).optional(),
  voiceClusterId: z.string().trim().min(1).max(300).optional(),
  speakerConfidence: z.number().min(0).max(1).optional(),
  accountConfidence: z.number().min(0).max(1).optional(),
  evidence: z.array(z.enum(["acoustic_diarization", "browser_acoustic_cluster", "meet_visual_activity"])).max(4).optional(),
  at: z.string().datetime(),
});
export type MeetingSpeakerTranscript = z.infer<typeof MeetingSpeakerTranscriptSchema>;

export const MeetingParticipantSchema = z.object({
  participantId: z.string().trim().min(1).max(300).optional(),
  displayName: z.string().trim().min(1).max(200),
  isSelf: z.boolean().default(false),
  source: z.enum(["authenticated_profile", "meet_semantic", "meet_media_api"]),
  confidence: z.number().min(0).max(1),
});
export type MeetingParticipant = z.infer<typeof MeetingParticipantSchema>;

export const MeetingContextSnapshotSchema = z.object({
  platform: z.literal("google_meet"),
  participantCount: z.number().int().min(1).max(500).optional(),
  participants: z.array(MeetingParticipantSchema).max(500).default([]),
  rosterConfidence: z.enum(["high", "inferred", "unknown"]),
  captureSource: z.enum(["none", "tab_capture", "meet_media_api"]),
  transcriptActive: z.boolean(),
  updatedAt: z.string().datetime(),
});
export type MeetingContextSnapshot = z.infer<typeof MeetingContextSnapshotSchema>;

export const MeetingSessionRecordSchema = z.object({
  id: z.string().uuid(),
  meetingWorkspaceId: z.string().uuid(),
  opaqueMeetingKey: z.string().regex(/^[A-Za-z0-9_-]{32,128}$/u),
  slackTeamId: z.string().min(1),
  requesterSlackUserId: z.string().regex(/^U[A-Z0-9]+$/u),
  requesterPersonId: z.string().min(1),
  status: z.enum(["active", "ended"]),
  context: MeetingContextSnapshotSchema,
  startedAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  endedAt: z.string().datetime().optional(),
});
export type MeetingSessionRecord = z.infer<typeof MeetingSessionRecordSchema>;

export const MeetingInputBoundarySchema = z.object({
  status: z.enum(["inactive", "connecting", "protected", "degraded", "error"]),
  source: z.enum(["none", "meet_media_api", "tab_capture"]),
  participantAttribution: z.enum(["none", "account", "participant", "unattributed"]),
  requesterAuthority: z.literal("authenticated_microphone_only"),
  message: z.string().trim().min(1).max(240),
});
export type MeetingInputBoundary = z.infer<typeof MeetingInputBoundarySchema>;

export const MeetingInputHealthSchema = z.object({
  status: z.enum(["not_expected", "waiting_for_signal", "signal_detected", "speech_detected", "transcript_confirmed", "failed"]),
  participantCount: z.number().int().min(1).max(500),
  sourceId: z.string().trim().min(1).max(300).optional(),
  remoteParticipantConfirmed: z.boolean(),
  trackAttachedAt: z.string().datetime().optional(),
  signalDetectedAt: z.string().datetime().optional(),
  speechDetectedAt: z.string().datetime().optional(),
  transcriptConfirmedAt: z.string().datetime().optional(),
  failureReason: z.enum(["remote_speaker_without_audio", "speech_without_transcript", "source_failed"]).optional(),
  signalCount: z.number().int().min(0),
  transcriptCount: z.number().int().min(0),
  updatedAt: z.string().datetime(),
});
export type MeetingInputHealth = z.infer<typeof MeetingInputHealthSchema>;

export const RealtimeEndReasonSchema = z.enum(["user", "idle_timeout", "agent_switch", "thread_closed", "error", "page_unload"]);
export type RealtimeEndReason = z.infer<typeof RealtimeEndReasonSchema>;

export const UsageKindSchema = z.enum(["realtime_response", "input_transcription", "codex_task", "observer_research", "text_message"]);
export type UsageKind = z.infer<typeof UsageKindSchema>;

export const UsageEventInputSchema = z.object({
  sourceEventId: z.string().min(1).max(200),
  kind: UsageKindSchema,
  inputTextTokens: z.number().int().nonnegative().default(0),
  inputAudioTokens: z.number().int().nonnegative().default(0),
  outputTextTokens: z.number().int().nonnegative().default(0),
  outputAudioTokens: z.number().int().nonnegative().default(0),
  durationMs: z.number().int().nonnegative().optional(),
});
export type UsageEventInput = z.infer<typeof UsageEventInputSchema>;

export const ConnectionStateSchema = z.enum([
  "idle",
  "connecting",
  "listening",
  "thinking",
  "speaking",
  "error",
]);
export type ConnectionState = z.infer<typeof ConnectionStateSchema>;

export const TaskCapabilitySchema = z.enum([
  "observer_hub",
  "observer_work",
  "observer_checkpoint",
  "workspace_search",
  "selects_specs",
  "web_search",
  "estimate_invoice",
  "image_generation",
  "calendar",
  "lemo_today",
  "lemo_files",
  "short_link",
  "local_files",
  "other",
]);
export type TaskCapability = z.infer<typeof TaskCapabilitySchema>;

export const ObserverCueIntentSchema = z.enum(["acknowledge", "answer", "clarify"]);
export type ObserverCueIntent = z.infer<typeof ObserverCueIntentSchema>;

export const ObserverCueDraftSchema = z.object({
  intent: ObserverCueIntentSchema,
  headline: z.string().trim().min(1).max(240).optional(),
  supportingFacts: z.array(z.string().trim().min(1).max(240)).max(2).default([]),
  pending: z.string().trim().min(1).max(240).optional(),
  mustNotClaim: z.array(z.string().trim().min(1).max(240)).max(6).default([]),
  maxSentences: z.union([z.literal(1), z.literal(2)]).default(2),
}).refine((value) => Boolean(value.headline || value.pending), { message: "headline or pending is required" });
export type ObserverCueDraft = z.infer<typeof ObserverCueDraftSchema>;

export const ObserverCuePacketSchema = ObserverCueDraftSchema.and(z.object({
  meetingTurnId: z.string().min(1).max(128),
  issuedAt: z.string().datetime(),
  expiresAt: z.string().datetime(),
}));
export type ObserverCuePacket = z.infer<typeof ObserverCuePacketSchema>;

export const ObserverWorkCapabilitySchema = z.enum(["workspace_search", "selects_specs", "web_search", "local_files", "other"]);
export type ObserverWorkCapability = z.infer<typeof ObserverWorkCapabilitySchema>;

const ObserverWorkKeySchema = z.string().regex(/^[\p{L}\p{N}._:-]{1,120}$/u);

export const ObserverWorkOrderSchema = z.object({
  id: ObserverWorkKeySchema,
  topicKey: ObserverWorkKeySchema,
  capability: ObserverWorkCapabilitySchema,
  objective: z.string().trim().min(1).max(2_000),
  activitySummary: z.string().trim().min(1).max(80).optional(),
  deliveryMode: z.enum(["internal", "thread"]).default("internal"),
});
export type ObserverWorkOrder = z.infer<typeof ObserverWorkOrderSchema>;

export const ObserverWorkActivityStateSchema = z.enum(["queued", "running", "completed", "failed"]);
export type ObserverWorkActivityState = z.infer<typeof ObserverWorkActivityStateSchema>;

export const ObserverWorkActivitySchema = z.object({
  activityId: z.string().regex(/^[A-Za-z0-9_-]{12,32}$/u),
  summary: z.string().trim().min(1).max(80),
  state: ObserverWorkActivityStateSchema,
  updatedAt: z.string().datetime(),
});
export type ObserverWorkActivity = z.infer<typeof ObserverWorkActivitySchema>;

export const ObserverHubDecisionSchema = z.object({
  decision: z.enum(["silent", "realtime", "cue"]),
  meetingBrief: z.string().trim().max(600).default(""),
  cue: ObserverCueDraftSchema.optional(),
  workOrders: z.array(ObserverWorkOrderSchema).max(3).default([]),
  deliverWorkOrderId: ObserverWorkKeySchema.optional(),
}).superRefine((value, context) => {
  if (value.decision === "cue" && !value.cue) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ["cue"], message: "cue is required when decision is cue" });
  }
  if (value.decision !== "cue" && value.cue) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ["cue"], message: "cue is only allowed when decision is cue" });
  }
  if (value.deliverWorkOrderId && value.workOrders.length > 0) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ["deliverWorkOrderId"], message: "delivery acceptance cannot create more work" });
  }
});
export type ObserverHubDecision = z.infer<typeof ObserverHubDecisionSchema>;

export const ObserverWorkResultSchema = z.object({
  workOrderId: ObserverWorkKeySchema,
  topicKey: ObserverWorkKeySchema,
  result: z.string().trim().min(1).max(12_000),
  sources: z.array(z.string().trim().min(1).max(500)).max(12).default([]),
  uncertainty: z.string().trim().min(1).max(1_000).optional(),
});
export type ObserverWorkResult = z.infer<typeof ObserverWorkResultSchema>;

export const TaskDeliveryModeSchema = z.enum(["thread", "internal"]);
export type TaskDeliveryMode = z.infer<typeof TaskDeliveryModeSchema>;

export const TaskRequestSchema = z.object({
  id: z.string().uuid(),
  slackTeamId: z.string().min(1).optional(),
  requesterPersonId: z.string().min(1).optional(),
  requesterSlackUserId: z.string().regex(/^U[A-Z0-9]+$/u),
  agent: AgentIdSchema,
  sessionId: z.string().uuid().optional(),
  threadId: z.string().uuid().optional(),
  runtimeSessionId: z.string().uuid().optional(),
  meetingSessionId: z.string().uuid().optional(),
  meetingWorkspaceId: z.string().uuid().optional(),
  surface: SessionSurfaceSchema,
  capability: TaskCapabilitySchema,
  deliveryMode: TaskDeliveryModeSchema.optional(),
  parentTaskId: z.string().uuid().optional(),
  workOrderId: ObserverWorkKeySchema.optional(),
  orchestrationDepth: z.number().int().min(0).max(4).optional(),
  prompt: z.string().trim().min(1).max(20_000),
  idempotencyKey: z.string().min(16).max(128),
  createdAt: z.string().datetime(),
  deadlineAt: z.string().datetime(),
}).refine((value) => Boolean(value.threadId || value.sessionId || value.meetingSessionId), { message: "threadId, sessionId, or meetingSessionId is required" });
export type TaskRequest = z.infer<typeof TaskRequestSchema>;

export const TaskStateSchema = z.enum([
  "queued",
  "leased",
  "running",
  "approval_required",
  "completed",
  "failed",
  "cancelled",
  "expired",
]);
export type TaskState = z.infer<typeof TaskStateSchema>;

export const TaskEventSchema = z.object({
  taskId: z.string().uuid(),
  state: TaskStateSchema,
  at: z.string().datetime(),
  publicMessage: z.string().max(1_000).optional(),
  resultMarkdown: z.string().max(100_000).optional(),
  errorCode: z.string().max(100).optional(),
});
export type TaskEvent = z.infer<typeof TaskEventSchema>;

export const AudioControlMessageSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("basket.audio.gate"), open: z.boolean() }),
  z.object({ type: z.literal("basket.audio.tone"), enabled: z.boolean(), frequencyHz: z.number().min(100).max(2_000).default(523.25) }),
  z.object({ type: z.literal("basket.audio.mute"), muted: z.boolean() }),
  z.object({ type: z.literal("basket.audio.stop") }),
  z.object({ type: z.literal("basket.audio.health.request") }),
]);
export type AudioControlMessage = z.infer<typeof AudioControlMessageSchema>;

export const AudioBridgeEventSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("basket.audio.ready"), version: z.literal(1) }),
  z.object({ type: z.literal("basket.audio.track"), physicalTrack: z.boolean(), mixedTrack: z.boolean() }),
  z.object({ type: z.literal("basket.audio.health"), gateOpen: z.boolean(), meetMuted: z.boolean(), senderCount: z.number().int().nonnegative() }),
  z.object({ type: z.literal("basket.audio.error"), code: z.string(), message: z.string() }),
]);
export type AudioBridgeEvent = z.infer<typeof AudioBridgeEventSchema>;

export const ConversationTurnSchema = z.object({
  id: z.string().uuid(),
  sessionId: z.string().uuid(),
  role: z.enum(["user", "assistant", "tool"]),
  text: z.string().max(100_000),
  at: z.string().datetime(),
});
export type ConversationTurn = z.infer<typeof ConversationTurnSchema>;

export function conversationLabel(turns: readonly Pick<ConversationTurn, "role" | "text">[]): string {
  const latest = [...turns].reverse().find((turn) => turn.role === "user" && turn.text.trim());
  if (!latest) return "新しい会話";
  const normalized = latest.text.replace(/\s+/gu, " ").trim();
  return normalized.length <= 42 ? normalized : `${normalized.slice(0, 41)}…`;
}
